import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
    ChevronDown,
    ChevronRight,
    Info,
    ShieldAlert,
    ShieldCheck,
    ShieldQuestion,
    AlertTriangle,
    AlertCircle,
    CircleAlert,
    CircleMinus,
    Search,
    Crosshair,
    Sparkles,
    Ticket,
    HardDrive,
    Layers,
    List,
    Wrench,
    Eye,
    UserCheck,
    RefreshCw,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { FeatheredScrollArea } from '@/components/ui/feathered-scroll-area'
import { buildNodeIconPath } from '@/lib/imagePaths'
import { EntityIcon } from '@/components/ui/entity-icon'
import { servicenowCmdbApi } from '@/features/servicenow-cmdb/api'
import { RaiseIncidentDialog, type IncidentPrefill } from './RaiseIncidentDialog'
import { parseScanCoverage, isCoverageNotice, parseCopyCheck, measuredBackups } from '@/lib/utils/scan-coverage'
import { MitreBadge, tacticForTechnique } from './MitreBadge'
import { ADFindingsSection } from './ADFindingsSection'
import { adAudit } from '@/lib/utils/ad-findings'
import { malwareEngine } from '@/lib/utils/malware-engine'
import { engineRoster } from '@/lib/utils/engine-roster'
import { groupFindings, topClusters, type FindingGroup } from '@/lib/utils/finding-groups'
import {
    canonicalEngine, confirmedEngineLabels, detectionClassOf, dispositionKeyOf, engineLabel, failedEngineLabels,
    isConfirmedCapable,
} from '@/lib/utils/scan-classification'
import { formatDateTime } from '@/lib/dateUtils'
import { useCan } from '@/hooks/useCan'
import type { ObjectVersion } from '@/types/entities'
import { ScanStatusChangePanel } from './ScanStatusChangePanel'
import { ScanRerunPanel } from './ScanRerunPanel'
import { useRequestScan } from '../scan-requests'

interface AnalyticsReportDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    analyticsText: string | null
    objectId?: string | null
    objectDescription?: string | null
    iconFilename?: string | null
    /** The version the report belongs to. With it, the report offers and shows a person's change to
     *  the status (two people: request, then approve). Without it the report is read-only. */
    version?: ObjectVersion | null
}

// Keyed by engine ROW name (see canonicalEngine in scan-classification). Look a row up through
// canonicalEngine: older scans send `ad_analysis` as a row name, and the roster derives one.
const ENGINE_DESCRIPTIONS: Record<string, string> = {
    ClamAV: 'Open-source antivirus engine that scans for known malware signatures, trojans, viruses, and other malicious threats.',
    Bitdefender: 'Commercial antivirus engine used on the premium tier in place of ClamAV. Scans for known malware signatures.',
    YARA: 'Pattern matching engine that identifies malware families using custom rules based on textual or binary patterns.',
    Entropy: 'Analyses file entropy to detect packed, encrypted, or obfuscated content that may indicate malware or ransomware.',
    HashLookup: 'Compares file hashes against known threat intelligence databases to identify previously catalogued malicious files.',
    ConfigDiff: 'Compares current configuration against a known-good baseline to detect unauthorized changes or tampering.',
    Bedrock: 'AI-powered analysis engine using Claude to perform deep inspection of configurations and detect subtle security issues.',
    BinaryAnalysis: 'Examines binary file structure, imports, and embedded strings to identify suspicious executables or payloads.',
    FilesystemAnomaly: 'Scans filesystem metadata for unusual permissions, hidden files, timestamp anomalies, or unexpected file placements.',
    Rootkit: 'Detects rootkit indicators including hidden processes, kernel modifications, and system call table tampering.',
    RansomNotes: 'Scans the filesystem for ransom note files (e.g. README / DECRYPT / HOW-TO-RESTORE text and HTML files) dropped by ransomware after encryption. A confirmed match is a strong indicator that an encryption event took place on the host.',
    RansomExtensions: 'Detects files renamed with extensions used by known ransomware families (e.g. .locked, .crypt, .encrypted and family-specific suffixes). Matches indicate that files on the backup may have been encrypted by ransomware.',
    MBR: 'Inspects the Master Boot Record and boot sectors for tampering by bootkits or wiper malware — overwritten boot code or partition tables that can render a restored system unbootable or hide a persistent implant.',
    EventLog: 'Parses Windows Event Logs for indicators of compromise — privileged account creation, audit-log clearing, suspicious service or scheduled-task installs, and anomalous or brute-forced logons — that can corroborate an attack even when no file-based malware is present.',
    ADAnalysis: 'Offline Active Directory attack audit, run only when the object is a Windows domain controller. Reads Group Policy and SYSVOL, the security event log, registry credential-protection posture, Certificate Services templates, Netlogon enforcement and the NTDS.dit directory database. Its findings are domain-wide: restoring a compromised controller restores the compromise with it.',
    StructuralValidation: 'Checks database backup files are structurally intact — not truncated, empty or encrypted.',
    WholeFileEntropy: 'Reads every byte of a native database backup and measures how much of it is random data. Random blocks in a backup can mean it was encrypted. Failed means a backup could not be measured, or was only partly read in the time allowed.',
    DbRiskPatterns: 'Looks for dual-use SQL constructs in database artefacts that are commonly abused for command execution or privilege escalation.',
    ThreatIntel: 'Correlates observed artefacts (file hashes, IP addresses, domains and filenames) against curated threat-intelligence feeds to flag known-bad indicators linked to malware, command-and-control infrastructure, or active campaigns.',
}

// Dark variants are explicit: these badges are now rendered on grouped rows in both
// themes, and a light-red pill on a dark card is unreadable.
const SEVERITY_CONFIG = {
    critical: { color: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300', calm: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', icon: ShieldAlert, label: 'Critical' },
    high: { color: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300', calm: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', icon: AlertTriangle, label: 'High' },
    medium: { color: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300', calm: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', icon: AlertCircle, label: 'Medium' },
    low: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300', calm: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', icon: CircleAlert, label: 'Low' },
    info: { color: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400', calm: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', icon: CircleMinus, label: 'Info' },
} as const

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info'] as const

function SeverityBadge({ severity }: { severity: string }) {
    const config = SEVERITY_CONFIG[severity as keyof typeof SEVERITY_CONFIG] || SEVERITY_CONFIG.info
    const Icon = config.icon
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
            <Icon className="h-3 w-3" />
            {config.label}
        </span>
    )
}

// Indicators are ordered/badged by confidence (high/medium/low), not severity — a
// high-severity indicator must never be presented as a threat.
function ConfidenceBadge({ confidence }: { confidence?: string | null }) {
    const c = String(confidence || '').toLowerCase()
    const label = c ? `${c.charAt(0).toUpperCase()}${c.slice(1)} confidence` : 'Confidence unrated'
    const tone = c === 'high' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tone}`}>{label}</span>
}

// MITRE ATT&CK technique → tactic map. Sub-techniques fall back to their base ID.
function EngineStatusBadge({ status }: { status: string }) {
    const colors: Record<string, string> = {
        completed: 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300',
        complete: 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300',
        skipped: 'bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
        not_applicable: 'bg-slate-50 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
        not_run: 'bg-slate-50 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
        error: 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300',
        failed: 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300',
    }
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400'}`}>
            {status.replace(/_/g, ' ')}
        </span>
    )
}

function formatDuration(seconds: number | undefined | null): string {
    if (seconds == null) return '-'
    if (seconds < 1) return '<1s'
    return `${seconds.toFixed(1)}s`
}

// detection_class buckets. Status is driven by detection_class / class_counts, NOT
// severity. `confidence` (not severity) orders indicators.
const CONFIDENCE_ORDER = ['high', 'medium', 'low'] as const
const ASSESSMENT_LABEL: Record<string, string> = {
    confirmed_detections: 'Confirmed detections',
    heuristic_findings_only: 'Heuristic findings only',
    no_confirmed_detections: 'No confirmed detections',
}

// ── Disposition: the single classification that drives the whole report ───────────────
// Decision-SUPPORT, never a safety verdict. Three substantive states, driven by
// detection_class / class_counts: confirmed detections (Red), indicators-only (its OWN
// state — must NOT escalate like a threat, must NOT read as "safe"), and no confirmed
// detections (code 4 — NOT a clean bill of health).
type DispKey = 'threat' | 'info' | 'clean' | 'unknown' | 'error' | 'incomplete'
interface Disposition {
    key: DispKey
    Icon: typeof ShieldAlert
    banner: string            // banner container classes
    chip: string              // small accent chip classes
    confirmedEngines: string[]
    aiSummary: string | null  // dynamic AI summary when present
}

// Static label/sub/why are English fallbacks; the real values live in the i18n locale
// files (keyed by disposition, see objects.versions.disposition*). LEGAL: this is
// decision-support — never "safe to restore", "clean", "no threats" or any restore
// recommendation. Code 4 = "No confirmed detections", not a clean bill of health.
const DISPOSITION_STYLES: Record<DispKey, { Icon: typeof ShieldAlert; label: string; sub: string; why: string; banner: string; chip: string; accent: string }> = {
    threat: { Icon: ShieldAlert, label: 'Threats found', sub: 'At least one confirmed detection was reported. Review the detections below.', why: 'Confirmed detection(s) reported by: {{engines}}.', banner: 'border-red-300 bg-red-50 dark:border-red-800/60 dark:bg-red-950/30', chip: 'bg-red-600 text-white', accent: 'text-red-600 dark:text-red-400' },
    // Indicators only — heuristic / dual-use signals, none confirmed. Its OWN state:
    // amber (caution, undetermined), not red (not a threat) and not green (not "safe").
    info: { Icon: Info, label: 'Indicators only', sub: 'Heuristic or dual-use indicators were reported; none is a confirmed detection.', why: 'No signature / hash / IOC engine reported a confirmed detection — the findings are heuristic or dual-use signals.', banner: 'border-amber-300 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/30', chip: 'bg-amber-600 text-white', accent: 'text-amber-600 dark:text-amber-400' },
    // Code 4. RELABEL (legal): never "Clean" / "Successful" / "No threats" / "Safe" / "Passed".
    clean: { Icon: ShieldCheck, label: 'No confirmed detections', sub: 'No confirmed detections were reported. This is not a clean bill of health — see the limitations and decision ownership below.', why: 'Deterministic engines completed without a confirmed detection.', banner: 'border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40', chip: 'bg-slate-600 text-white', accent: 'text-slate-600 dark:text-slate-300' },
    unknown: { Icon: ShieldQuestion, label: 'Not scanned', sub: 'This version has not been scanned.', why: '', banner: 'border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40', chip: 'bg-slate-500 text-white', accent: 'text-slate-500' },
    // The scanner said the scan was incomplete, or an engine failed: never headed as a completed scan.
    incomplete: { Icon: AlertTriangle, label: 'Scan incomplete', sub: 'Part of this version was not examined. The findings below cover only what was examined; this is not a result for the whole version.', why: 'Did not finish: {{engines}}.', banner: 'border-amber-300 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/30', chip: 'bg-amber-600 text-white', accent: 'text-amber-600 dark:text-amber-400' },
    error: { Icon: AlertTriangle, label: 'Scan error', sub: 'The scan did not complete.', why: 'One or more engines failed to run.', banner: 'border-amber-300 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/30', chip: 'bg-amber-600 text-white', accent: 'text-amber-600 dark:text-amber-400' },
}

function deriveDisposition(data: any): Disposition {
    const ai = data.ai_analysis || null
    // Which headline, and which engines it names, are worked out in scan-classification: the
    // scanner's status and class counts, never the AI's assessment, never severity. Telemetry,
    // posture and advisory never move it.
    const key: DispKey = dispositionKeyOf(data)
    return {
        key,
        confirmedEngines: confirmedEngineLabels(data),
        aiSummary: ai && ai.summary ? String(ai.summary) : null,
        Icon: DISPOSITION_STYLES[key].Icon, banner: DISPOSITION_STYLES[key].banner, chip: DISPOSITION_STYLES[key].chip,
    }
}

// ── RAG, and why ADVISORY is welded to it ────────────────────────────────────────────
//
// Red/amber/green is the fastest status channel we have and it stays. The risk it carries
// is specific and worth naming: a GREEN can be read as "this object is fine to restore"
// when all it can honestly mean is "the engines we ran, over the volumes we reached, did
// not detect a known threat". A scanner cannot prove the absence of a threat.
//
// The mitigation is structural rather than editorial. Every RAG surface in this report
// carries its advisory qualifier in the SAME visual unit as the colour:
//   - the persistent header chip reads "ADVISORY · <status>", so no scroll position ever
//     shows the colour on its own;
//   - the status card wears the qualifier as a full-width strip across its top, above the
//     colour, not as a pill lost in a row of other pills.
// The qualifier is strongest exactly where the misreading is: green says "not a clean bill
// of health" in the strip itself.
type RagKey = 'red' | 'amber' | 'green' | 'neutral'

interface RagStyle {
    /** Left rail on the status card — the colour channel proper. */
    rail: string
    /** ADVISORY strip across the top of the card. */
    strip: string
    /** Card body. */
    card: string
    /** Persistent header chip. */
    chip: string
    /** Icon / number accent. */
    accent: string
    /** Bare dot, for the chip. */
    dot: string
    /** The advisory qualifier — the sentence that must never be separated from the colour. */
    qualifier: string
    qualifierKey: string
}

const RAG: Record<RagKey, RagStyle> = {
    red: {
        rail: 'bg-red-500',
        strip: 'bg-red-100 text-red-900 dark:bg-red-950/60 dark:text-red-200',
        card: 'border-red-300 bg-red-50/60 dark:border-red-900/60 dark:bg-red-950/20',
        chip: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200',
        accent: 'text-red-600 dark:text-red-400',
        dot: 'bg-red-500',
        qualifier: 'Advisory — decision support. Your security team owns the restore decision.',
        qualifierKey: 'objects.versions.ragQualifier_red',
    },
    amber: {
        rail: 'bg-amber-500',
        strip: 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200',
        card: 'border-amber-300 bg-amber-50/60 dark:border-amber-900/60 dark:bg-amber-950/20',
        chip: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
        accent: 'text-amber-600 dark:text-amber-400',
        dot: 'bg-amber-500',
        qualifier: 'Advisory — not a verdict. Review the indicators before restoring.',
        qualifierKey: 'objects.versions.ragQualifier_amber',
    },
    green: {
        rail: 'bg-green-500',
        strip: 'bg-green-100 text-green-900 dark:bg-green-950/60 dark:text-green-200',
        card: 'border-green-300 bg-green-50/60 dark:border-green-900/60 dark:bg-green-950/20',
        chip: 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-200',
        accent: 'text-green-600 dark:text-green-400',
        dot: 'bg-green-500',
        // The one that matters most. Green must never travel without this sentence.
        qualifier: 'Advisory — not a clean bill of health. Covers only what was scanned.',
        qualifierKey: 'objects.versions.ragQualifier_green',
    },
    neutral: {
        rail: 'bg-slate-400',
        strip: 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
        card: 'border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40',
        chip: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
        accent: 'text-slate-600 dark:text-slate-300',
        dot: 'bg-slate-400',
        qualifier: 'Advisory — coverage incomplete. Not everything could be scanned.',
        qualifierKey: 'objects.versions.ragQualifier_neutral',
    },
}

/**
 * Disposition + coverage → RAG.
 *
 * The only subtlety is the "no confirmed detections" case: GREEN only when coverage was
 * complete. Over a skipped volume it drops to NEUTRAL, because a confident green over a
 * gap is the exact misreading this report exists to avoid.
 */
function ragFor(disposition: DispKey, coverageIncomplete: boolean): RagKey {
    if (disposition === 'threat') return 'red'
    if (disposition === 'info') return 'amber'
    if (disposition === 'error' || disposition === 'unknown' || disposition === 'incomplete') return 'neutral'
    return coverageIncomplete ? 'neutral' : 'green'
}

/**
 * Collapsible block. The report carries a lot of genuinely useful prose — AI narrative,
 * limitations, per-volume coverage — that is valuable ON REQUEST and noise by default.
 * Everything long lives behind one of these; nothing is removed.
 */
function Disclosure({
    label, count, children, defaultOpen = false, className = '',
}: { label: string; count?: number | null; children: ReactNode; defaultOpen?: boolean; className?: string }) {
    const [open, setOpen] = useState(defaultOpen)
    return (
        <div className={className}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
                {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {label}
                {count != null && <span className="tabular-nums opacity-70">({count})</span>}
            </button>
            {open && <div className="mt-2">{children}</div>}
        </div>
    )
}

/**
 * Which malware engine produced the verdict.
 *
 * The tiers are mutually exclusive — free runs ClamAV, premium runs Bitdefender INSTEAD of it,
 * never both — and a report that does not say which ran cannot be defended when a customer
 * challenges a finding. So this sits with the overall status, not buried in the Engines tab:
 * engine identity qualifies the verdict itself.
 */
function MalwareEngineBadge({ report }: { report: any }) {
    const m = malwareEngine(report)

    if (m.conflict) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300"
                  title="Both ClamAV and Bitdefender report as complete. They are mutually exclusive, so this scan's engine attribution cannot be trusted.">
                <AlertTriangle className="h-3 w-3" />Two malware engines reported
            </span>
        )
    }

    // Not applicable is a statement about the object, not a gap in the scan: a switch configuration
    // has no executable content for a signature engine to match, and configuration analysis
    // replaces it. It must never read like the "not recorded" case below.
    if (m.notApplicable) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  title={m.notApplicable.reason}>
                <Info className="h-3 w-3" />
                Malware engine: <span className="font-semibold">not applicable</span>
            </span>
        )
    }

    if (!m.engine) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                  title="Neither ClamAV nor Bitdefender is recorded as having completed, and the scan did not declare one. It is not assumed.">
                <ShieldQuestion className="h-3 w-3" />Malware engine not recorded
            </span>
        )
    }

    const detail = [
        m.signature ? `signatures ${m.signature}` : null,
        m.tier ? `${m.tier} tier` : null,
        m.source === 'scanner' ? 'declared by the scanner; not listed in the engine summary' : null,
        m.replaced ? `${m.replaced.name}: ${m.replaced.reason}` : null,
    ].filter(Boolean).join(' · ')

    return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
              title={detail || undefined}>
            <ShieldCheck className="h-3 w-3" />
            Malware engine: <span className="font-semibold">{m.engine}</span>
            {m.signature && <span className="text-muted-foreground hidden sm:inline">· {m.signature}</span>}
        </span>
    )
}

/** One of the detection-class counts that replace the severity row as the headline. */
function ClassCount({
    n, label, tone, hint,
}: { n: number; label: string; tone: string; hint?: string }) {
    return (
        <div className="flex-1 min-w-0 rounded-lg border bg-background/70 dark:bg-black/20 px-3 py-2" title={hint}>
            <div className={`text-2xl font-bold tabular-nums leading-none ${n > 0 ? tone : 'text-muted-foreground/50'}`}>{n}</div>
            <div className="text-[11px] text-muted-foreground mt-1 leading-tight">{label}</div>
        </div>
    )
}

/**
 * One grouped cluster of findings — "364 × Suspicious API imports", expandable to the
 * individual files. This is the row that replaces 364 identical rows.
 */
function ClusterRow({
    group, expanded, onToggle, t,
}: { group: FindingGroup; expanded: boolean; onToggle: () => void; t: (k: string, d?: string, o?: any) => string }) {
    const { i18n } = useTranslation()
    const n = group.members.length
    const tone = group.detectionClass === 'confirmed'
        ? 'text-red-600 dark:text-red-400'
        : group.detectionClass === 'telemetry'
            ? 'text-muted-foreground'
            : group.detectionClass === 'posture'
                ? 'text-sky-700 dark:text-sky-300'
                : group.detectionClass === 'advisory'
                    ? 'text-violet-700 dark:text-violet-300'
                    : 'text-amber-600 dark:text-amber-400'

    // Render a bounded slice: a 364-member group must not put 364 nodes in the DOM.
    const SHOWN = 60
    const shown = group.members.slice(0, SHOWN)
    const hidden = n - shown.length

    return (
        <div className="border rounded-lg overflow-hidden">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
            >
                {expanded ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                <span className={`text-sm font-bold tabular-nums flex-shrink-0 min-w-[2.5rem] text-right ${tone}`}>{n}</span>
                <span className="text-sm font-medium flex-1 truncate"><TranslatableText text={group.label} /></span>
                {(group.detectionClass === 'confirmed' || group.detectionClass === 'posture') && group.severity && (
                    <span className="flex-shrink-0 hidden sm:inline-flex"><SeverityBadge severity={group.severity} /></span>
                )}
                {group.detectionClass === 'indicator' && group.confidence && (
                    <span className="flex-shrink-0 hidden sm:inline-flex"><ConfidenceBadge confidence={group.confidence} /></span>
                )}
                {group.mitre && <span className="flex-shrink-0 hidden md:inline-flex"><MitreBadge id={group.mitre} name={group.mitreName} compact /></span>}
                <span className="text-xs text-muted-foreground flex-shrink-0">{engineLabel(group.engine)}</span>
            </button>
            {expanded && (
                <div className="border-t bg-muted/30 px-4 py-3 space-y-3">
                    {/* The description is identical across the group — show it once, not n times. */}
                    {group.members[0]?.f?.description && (
                        <TranslatableText as="p" className="text-sm" text={group.members[0].f.description} />
                    )}
                    {group.mitre && (
                        <div className="flex flex-wrap items-center gap-2">
                            <MitreBadge id={group.mitre} name={group.mitreName} />
                            {tacticForTechnique(group.mitre) && (
                                <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">{tacticForTechnique(group.mitre)}</span>
                            )}
                        </div>
                    )}
                    {n > 1 && (
                        <div>
                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                                {t('objects.versions.affectedItems', 'Affected items')} <span className="tabular-nums">({n})</span>
                            </div>
                            <ul className="space-y-0.5 max-h-64 overflow-y-auto rounded border bg-background/60 dark:bg-black/20 p-2">
                                {shown.map(({ f, i }) => (
                                    <li key={i} className="text-xs font-mono text-muted-foreground truncate" title={f.details?.filepath || f.filepath || f.title}>
                                        {f.details?.filepath || f.filepath || f.title}
                                    </li>
                                ))}
                                {hidden > 0 && (
                                    <li className="text-xs text-muted-foreground italic pt-1">
                                        {t('objects.versions.andMore', '+{{n}} more', { n: hidden })}
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}
                    {n === 1 && group.members[0].f.details?.line && (
                        <pre className="bg-muted rounded p-2 text-xs font-mono whitespace-pre-wrap break-words overflow-x-auto">{group.members[0].f.details.line}</pre>
                    )}
                    {n === 1 && group.members[0].f.timestamp && (
                        <span className="text-xs text-muted-foreground">{formatDateTime(group.members[0].f.timestamp, i18n.language)}</span>
                    )}
                </div>
            )}
        </div>
    )
}

export function AnalyticsReportDialog({ open, onOpenChange, analyticsText, objectId, objectDescription, iconFilename, version }: AnalyticsReportDialogProps) {
    const { t } = useTranslation()
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
    const [search, setSearch] = useState('')
    const [tab, setTab] = useState('overview')
    const [incidentOpen, setIncidentOpen] = useState(false)
    // Raising an incident writes to ServiceNow: servicenow.update.
    const canRaiseIncident = useCan('servicenow.update')
    // Scanning the version again: scan.run. The button is in the header's action row; what became
    // of the request (queued, running, refused) is shown by ScanRerunPanel beneath.
    const canRunScan = useCan('scan.run')
    const rescan = useRequestScan()
    // Grouped is the default: a flat list of 609 rows is unreadable, and its LENGTH reads
    // as severity. The toggle keeps the raw list one click away for an analyst who wants it.
    const [grouped, setGrouped] = useState(true)

    // Is the ServiceNow integration usable, and does this object have a synced CI?
    // Drives the "Raise ServiceNow Incident" button and pre-fills the cmdb_ci reference.
    const incidentCtx = useQuery({
        queryKey: ['snIncidentContext', objectId],
        queryFn: () => servicenowCmdbApi.getIncidentContext(objectId!).then(r => r.data.data),
        enabled: !!open && !!objectId,
        staleTime: 60_000,
    })

    let data: any = null
    try {
        if (analyticsText) data = JSON.parse(analyticsText)
    } catch {
        // invalid JSON
    }

    const handleOpenChange = (o: boolean) => {
        if (!o) { setSearch(''); setExpandedGroups(new Set()); setTab('overview'); setGrouped(true); rescan.reset() }
        onOpenChange(o)
    }

    if (!data) {
        return (
            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{t('objects.versions.analyticsReport', 'Security Scan Report')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex items-center gap-3 rounded-lg border border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40 p-4">
                        <ShieldQuestion className="h-6 w-6 text-slate-500 flex-shrink-0" />
                        <div>
                            <div className="font-semibold text-sm">{t('objects.versions.notScanned', 'Not scanned')}</div>
                            <div className="text-xs text-muted-foreground">{t('objects.versions.noAnalyticsData', 'No scan report is available for this version.')}</div>
                        </div>
                    </div>
                    <ScanRerunPanel versionId={version?.versionID} scanRequest={version?.scanRequest} />
                </DialogContent>
            </Dialog>
        )
    }

    // Device fields may live under metadata or (depending on the scanner) at the top level —
    // merge the top-level ones in so Device Information renders either way.
    const metadata: Record<string, any> = { ...(data.metadata || {}) }
    for (const k of ['device_name', 'manufacturer', 'model', 'software_version', 'ip_address', 'default_gateway', 'software_image_file']) {
        if (metadata[k] == null && data[k] != null) metadata[k] = data[k]
    }
    const summary = data.summary || {}
    const severityCounts: Record<string, number> = summary.severity_counts || {}
    const engines = data.engines || {}
    const findings = data.findings || data.top_findings || []
    const ai = data.ai_analysis || null
    // ai_analysis can be present but carry only an error (e.g. the model call failed), in which
    // case it has no summary/verdict/findings. Treat it as "no usable AI content" so we show the
    // fallback message instead of a blank panel.
    const aiErrored = !!(ai && (ai.status === 'error' || ai.error))
    const aiHasContent = !!(ai && !aiErrored && (
        ai.summary || ai.status_line || ai.assessment || ai.attack_narrative ||
        (Array.isArray(ai.key_findings) && ai.key_findings.length > 0) ||
        (Array.isArray(ai.likely_false_positives) && ai.likely_false_positives.length > 0)
    ))
    // The verbose narrative (summary / attack / key findings) lives on its own "Detailed
    // analysis" tab; Overview keeps just the headline + the always-on disclaimers.
    const aiHasDetail = !!(ai && !aiErrored && (
        ai.summary || ai.attack_narrative ||
        (Array.isArray(ai.key_findings) && ai.key_findings.length > 0) ||
        (Array.isArray(ai.likely_false_positives) && ai.likely_false_positives.length > 0)
    ))

    const disposition = deriveDisposition(data)
    const isThreat = disposition.key === 'threat'
    const failedEngines = failedEngineLabels(data)

    // Coverage picture — a scanner cannot prove absence of a threat, so a "no confirmed
    // detections" result over INCOMPLETE coverage must not read as a confident all-clear.
    const coverage = parseScanCoverage(data)
    const skippedVolumes = coverage.volumes.filter((v) => v && v.scanned === false)
    // From the Scanner session, 8 Oct 2026: how each file was tied to the copy, and how much of
    // each database backup was read (measured raw, checked against its format, or both).
    const copyCheck = parseCopyCheck(data)
    const measured = measuredBackups(data)

    // RAG + its inseparable advisory qualifier.
    const ragKey = ragFor(disposition.key, coverage.incomplete)
    const rag = RAG[ragKey]
    const coverageDowngrade = disposition.key === 'clean' && coverage.incomplete

    const pipelineFindings = findings.filter((f: any) => f.engine === 'pipeline')

    // Active Directory is read from `ad_findings`, attached server-side. It is NOT in the
    // report's own findings list: that list is a truncated top-N with `details` stripped, and
    // details.attack is the whole point of an AD finding.
    const ad = adAudit(data)

    // The engine summary is a fixed whitelist and currently omits two engines that demonstrably
    // ran: the premium malware engine, and ad_analysis. An engine that ran but is not listed
    // reads exactly like one that did not run, which on a security report understates coverage.
    const engineRows = engineRoster(data, ad.findings.length + (ad.coverageGap ? 1 : 0))

    const reportTechniques: Array<{ id: string; name?: string | null; count?: number }> = data.attack_techniques || []
    const observedTactics = Array.from(new Set([
        ...reportTechniques.map((tt) => tacticForTechnique(tt.id)),
        ...findings.map((f: any) => tacticForTechnique(f.details?.mitre_attack)),
    ].filter(Boolean))) as string[]
    const hasAttack = observedTactics.length > 0 || reportTechniques.length > 0

    // Engine summary for the overview
    const engineEntries = Object.entries(engines) as Array<[string, any]>
    const enginesRan = engineEntries.filter(([, e]) => e.status === 'completed' || e.status === 'complete').length
    const enginesWithFindings = engineEntries.filter(([, e]) => (e.findings || 0) > 0)
    // not_applicable is NOT counted as not run: it is an engine that has nothing to inspect here
    // (a switch config has no executables for a signature engine) or was replaced by another.
    // Counting it with the failures reads as lost coverage, which it is not.
    const enginesNotRun = engineEntries.filter(([, e]) => e.status === 'not_run' || e.status === 'skipped').length

    const toggleGroup = (key: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key); else next.add(key)
            return next
        })
    }

    const q = search.toLowerCase()
    // Findings are grouped into three detection_class buckets (NOT a flat severity list).
    // Each item keeps its original index so the expand/collapse state stays stable.
    const matchesSearch = (f: any) => !search || (
        f.title?.toLowerCase().includes(q) ||
        f.description?.toLowerCase().includes(q) ||
        f.confidence?.toLowerCase().includes(q) ||
        f.classification?.toLowerCase().includes(q) ||
        f.engine?.toLowerCase().includes(q) ||
        f.details?.filepath?.toLowerCase().includes(q) ||
        f.details?.mitre_attack?.toLowerCase().includes(q) ||
        (tacticForTechnique(f.details?.mitre_attack) || '').toLowerCase().includes(q)
    )
    const indexed: Array<{ f: any; i: number }> = findings
        .map((f: any, i: number) => ({ f, i }))
        .filter(({ f }: { f: any }) => f.engine !== 'pipeline' && matchesSearch(f))
    const bySeverity = (a: { f: any }, b: { f: any }) =>
        SEVERITY_ORDER.indexOf((a.f.severity || 'info').toLowerCase() as any) - SEVERITY_ORDER.indexOf((b.f.severity || 'info').toLowerCase() as any)
    const byConfidence = (a: { f: any }, b: { f: any }) =>
        CONFIDENCE_ORDER.indexOf((a.f.confidence || 'low').toLowerCase() as any) - CONFIDENCE_ORDER.indexOf((b.f.confidence || 'low').toLowerCase() as any)

    // Confirmed = Detections (Red). Sort by severity (a secondary hint, confirmed only).
    const confirmedFindings = indexed.filter(({ f }) => detectionClassOf(f) === 'confirmed').sort(bySeverity)
    // Indicators = heuristic / dual-use, uncorroborated. Sort by confidence, NOT severity.
    const allIndicators = indexed.filter(({ f }) => detectionClassOf(f) === 'indicator')
    const coverageFindings = allIndicators.filter(({ f }) => isCoverageNotice(f))
    const indicatorFindings = allIndicators.filter(({ f }) => !isCoverageNotice(f)).sort(byConfidence)
    // Telemetry = routine dual-use. Context only; never alarming.
    const telemetryFindings = indexed.filter(({ f }) => detectionClassOf(f) === 'telemetry')
    // Posture = configuration hardening. Its own section, never folded into indicators. Ordered by
    // severity, which still means impact-if-real: a HIGH posture finding is worth reading, it is
    // just not a reason to hold a restore — so it is not sunk to the bottom on severity either.
    const postureFindings = indexed.filter(({ f }) => detectionClassOf(f) === 'posture').sort(bySeverity)
    // Advisory = an interpretation (the AI reading configuration text) or an observed change (the
    // config diff). Not a detection, so never an indicator and never amber; its own section, ahead
    // of posture. Ordered by severity like posture, but the row shows no severity badge: a "High"
    // pill on an opinion is how an opinion comes to read as a threat.
    const advisoryFindings = indexed.filter(({ f }) => detectionClassOf(f) === 'advisory').sort(bySeverity)

    // ── Grouping: the readability fix ────────────────────────────────────────────────
    // 609 findings on the AD1 scan are three observations repeated per file. Measured on
    // ordb's whole analytics_findings corpus: 37,573 findings / 7,368 distinct titles
    // collapse to 262 groups, with ClamAV signatures and YARA rule names left intact.
    const confirmedGroups = groupFindings(confirmedFindings, detectionClassOf)
    const indicatorGroups = groupFindings(indicatorFindings, detectionClassOf)
    const coverageGroups = groupFindings(coverageFindings, detectionClassOf)
    const telemetryGroups = groupFindings(telemetryFindings, detectionClassOf)
    const postureGroups = groupFindings(postureFindings, detectionClassOf)
    const advisoryGroups = groupFindings(advisoryFindings, detectionClassOf)
    // "What was flagged" is the threat picture; advisory and posture have their own sections
    // beneath it.
    const allGroups = [...confirmedGroups, ...indicatorGroups, ...telemetryGroups]
    // WHAT the confirmed detections and indicators are, named in the headline card itself.
    // Andrew, 9 Oct 2026, on a report headed "Indicators only - 1 Indicators": "Nothing seems to
    // show what the indicator is." It was listed, but below the Active Directory section, a
    // screen further down. The count and the thing counted belong together.
    const namedGroups = [...confirmedGroups, ...indicatorGroups].slice(0, 5)
    const namedKeys = new Set(namedGroups.map((g) => g.key))
    const namedMore = confirmedGroups.length + indicatorGroups.length - namedGroups.length
    // "What was flagged", further down, carries on from there and does not repeat them.
    const headlineClusters = topClusters(allGroups.filter((g) => !namedKeys.has(g.key)), 5)

    const classCounts = (summary.class_counts || {}) as Record<string, number>
    const confirmedN = Number(classCounts.confirmed ?? confirmedFindings.length)
    const indicatorN = Number(classCounts.indicator ?? (indicatorFindings.length + coverageFindings.length))
    const telemetryN = Number(classCounts.telemetry ?? telemetryFindings.length)
    const postureN = Number(classCounts.posture ?? postureFindings.length)
    const advisoryN = Number(classCounts.advisory ?? advisoryFindings.length)

    const totalFindings = summary.total_findings ?? findings.length
    const totalGroups = allGroups.length + coverageGroups.length + advisoryGroups.length + postureGroups.length

    // Headline label/sub. "No confirmed detections" over COMPLETE coverage is the green
    // case and says so; over a gap it stays neutral and says that instead.
    const cleanComplete = disposition.key === 'clean' && !coverage.incomplete
    const bannerKey = cleanComplete ? 'noThreats' : disposition.key
    const bannerLabel = cleanComplete ? 'No confirmed detections' : DISPOSITION_STYLES[disposition.key].label
    const bannerSub = cleanComplete
        ? 'No confirmed detections were reported in what we scanned.'
        : DISPOSITION_STYLES[disposition.key].sub
    const bannerWhy = cleanComplete ? 'Deterministic engines completed the scan without a confirmed detection.' : DISPOSITION_STYLES[disposition.key].why
    const DispIcon = cleanComplete ? ShieldCheck : disposition.Icon

    // Pre-fill the incident from the scan. urgency/impact track the disposition
    // (confirmed detections → High). The rest of the mandatory fields are filled by the user.
    const dispLabel = DISPOSITION_STYLES[disposition.key].label
    const prio = disposition.key === 'threat' ? '1' : disposition.key === 'info' ? '2' : '3'
    const incidentPrefill: IncidentPrefill = {
        shortDescription: `VaultSentinel: ${dispLabel} — ${objectId || 'object'}`.slice(0, 160),
        description: [
            `VaultSentinel security scan disposition: ${dispLabel}.`,
            objectId ? `Object: ${objectId}${objectDescription ? ` (${objectDescription})` : ''}` : null,
            data.scan_id ? `Scan ID: ${data.scan_id}` : null,
            data.scan_type ? `Scan type: ${data.scan_type}` : null,
            `Confirmed detections: ${confirmedN}; indicators: ${indicatorN}; flagged for review: ${advisoryN}; configuration hardening: ${postureN}; total findings: ${totalFindings}.`,
            ai?.status_line ? `AI assessment: ${ai.status_line}` : (ai?.assessment ? `AI assessment: ${ai.assessment}` : null),
            `Raised from VaultSentinel analytics.`,
        ].filter(Boolean).join('\n'),
        urgency: prio,
        impact: prio,
        correlationId: String(data.scan_id || objectId || ''),
    }

    const renderGroups = (groups: FindingGroup[]) =>
        groups.map((g) => (
            <ClusterRow key={g.key} group={g} expanded={expandedGroups.has(g.key)} onToggle={() => toggleGroup(g.key)} t={t as any} />
        ))

    // Ungrouped fallback — one synthetic single-member group per finding, so the flat list
    // reuses exactly the same row component and expand state.
    const asSingles = (items: Array<{ f: any; i: number }>): FindingGroup[] =>
        items.map(({ f, i }) => ({
            key: `single-${i}`,
            label: String(f.title || 'Untitled finding'),
            engine: String(f.engine || ''),
            detectionClass: detectionClassOf(f),
            confidence: f.confidence || null,
            severity: f.severity || null,
            mitre: f.details?.mitre_attack || null,
            mitreName: f.details?.mitre_technique || null,
            members: [{ f, i }],
            collapsed: false,
        }))

    return (
        <>
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-4xl h-[90vh] overflow-hidden flex flex-col gap-0 p-0">
                <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">

                    {/* ── STICKY CHROME ────────────────────────────────────────────────
                        Deliberately small. Everything substantive scrolls in ONE region
                        below, instead of a letterbox under a frozen wall of text. What
                        stays is only what must: identity, the RAG-with-advisory chip, and
                        navigation. */}
                    <div className="flex-shrink-0 border-b bg-background">
                        <DialogHeader className="px-6 pt-5 pb-0 space-y-0 text-left">
                            <DialogTitle className="text-base">{t('objects.versions.analyticsReport', 'Security Scan Report')}</DialogTitle>
                            <p className="text-xs text-muted-foreground">
                                {data.scan_type && `${data.scan_type.charAt(0).toUpperCase() + data.scan_type.slice(1)} scan`}
                                {data.scan_id && ` • ${data.scan_id}`}
                                {data.duration_seconds != null && ` • ${data.duration_seconds}s`}
                            </p>
                        </DialogHeader>

                        <div className="px-6 pt-3 flex items-center gap-3">
                            {objectId && (
                                <>
                                    <EntityIcon src={buildNodeIconPath(iconFilename)} alt="" className="h-8 w-8 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-sm font-bold truncate leading-tight">{objectId}</div>
                                        {objectDescription && (
                                            <TranslatableText as="div" className="text-xs text-muted-foreground truncate leading-tight" text={objectDescription} />
                                        )}
                                    </div>
                                </>
                            )}
                            <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                                {/* Which malware engine produced the verdict, on EVERY tab. Reading
                                    the Scanning Engines tab alone, ClamAV stands down and nothing
                                    visibly replaces it — the answer has to travel with the status. */}
                                <span className="hidden md:inline-flex"><MalwareEngineBadge report={data} /></span>
                                {/* PERSISTENT RAG + ADVISORY. The colour never appears without the
                                    word next to it, at any scroll position, on any tab. */}
                                <span
                                    className={`inline-flex items-center gap-2 rounded-full pl-2.5 pr-3 py-1 ${rag.chip}`}
                                    title={t(rag.qualifierKey, rag.qualifier)}
                                >
                                    <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${rag.dot}`} />
                                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">
                                        {t('objects.versions.advisory', 'Advisory')}
                                    </span>
                                    <span className="text-xs font-semibold">
                                        {t('objects.versions.disposition_' + bannerKey, bannerLabel)}
                                    </span>
                                </span>
                                {/* A person's change travels with the status on every tab: the chip to its
                                    left is what the SCAN concluded, and this says the status is no longer that. */}
                                {version?.analyticsSource === 'override' && (
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-400 bg-slate-50 px-2.5 py-1 text-xs font-semibold dark:border-slate-600 dark:bg-slate-900/40">
                                        <UserCheck className="h-3.5 w-3.5" />
                                        {t('objects.statusChange.chip')}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* The tabs, and the report's actions on the SAME line as the tabs - not on the
                            line above with the status chips, where "Raise ServiceNow Incident" ran off
                            the edge of the dialog (Andrew, 6 Oct). They wrap beneath the tabs when the
                            dialog is narrow, so no button is ever cut off. */}
                        <div className="mx-6 my-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                        <TabsList className="w-auto">
                            <TabsTrigger value="overview">{t('objects.versions.tabOverview', 'Overview')}</TabsTrigger>
                            {aiHasDetail && <TabsTrigger value="detailed">{t('objects.versions.tabDetailed', 'Detailed analysis')}</TabsTrigger>}
                            <TabsTrigger value="findings">{t('objects.versions.findings', 'Findings')}{totalFindings ? ` (${totalFindings})` : ''}</TabsTrigger>
                            {hasAttack && <TabsTrigger value="attack">ATT&CK</TabsTrigger>}
                            <TabsTrigger value="engines">{t('objects.versions.scanningEngines', 'Engines')}</TabsTrigger>
                        </TabsList>
                            <div className="ml-auto flex flex-wrap items-center gap-2">
                                {/* Not while a scan of this version is already queued or running. */}
                                {version?.versionID != null && canRunScan && !version.scanRequest?.open && (
                                    <Button
                                        size="sm"
                                        variant={disposition.key === 'error' ? 'default' : 'outline'}
                                        className="gap-2 h-8"
                                        disabled={rescan.isPending}
                                        onClick={() => rescan.mutate(version.versionID as number)}
                                    >
                                        <RefreshCw className={`h-4 w-4 ${rescan.isPending ? 'animate-spin' : ''}`} />
                                        {t('scanRequests.run')}
                                    </Button>
                                )}
                                {incidentCtx.data?.enabled && canRaiseIncident && (
                                    <Button size="sm" variant="outline" className="gap-2 h-8" onClick={() => setIncidentOpen(true)}>
                                        <Ticket className="h-4 w-4" />
                                        {t('objects.incident.button', 'Raise ServiceNow Incident')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── ONE SCROLL REGION ───────────────────────────────────────────── */}
                    <FeatheredScrollArea className="flex-1 min-h-0" viewportClassName="h-full px-6 py-4">

                        {/* ── A PERSON'S CHANGE TO THE STATUS ─────────────────────────────
                            Above the status card, on every tab. It records who asked, who
                            approved and why; it never replaces what the scan found, which
                            stays in the card and the findings beneath. */}
                        <ScanStatusChangePanel
                            versionId={version?.versionID}
                            status={version?.objectVersionStatus}
                            analyticsSource={version?.analyticsSource}
                            changes={version?.statusChanges}
                            referenceToAccept={version?.referenceToAccept === true}
                            scanLabel={t('objects.versions.disposition_' + bannerKey, bannerLabel)}
                        />

                        {/* ── SCANNING IT AGAIN ───────────────────────────────────────────
                            The scan's own failure in its own words, a scan that is queued or
                            running, and the button to run one. Primary when this report is a
                            failed scan: then scanning again is the thing to do. */}
                        <ScanRerunPanel
                            versionId={version?.versionID}
                            scanRequest={version?.scanRequest}
                            scanError={disposition.key === 'error' && typeof data.error === 'string' ? data.error : null}
                            failed={disposition.key === 'error'}
                            request={rescan}
                        />

                        {/* ── STATUS CARD: the headline answer ─────────────────────────
                            The ADVISORY qualifier is a full-width strip ACROSS THE TOP of
                            the card — structurally above the colour, not a pill lost in a
                            row of pills. You cannot read the green without reading "not a
                            clean bill of health" first. */}
                        <div className={`rounded-lg border overflow-hidden ${rag.card}`}>
                            <div className={`flex items-center gap-2 px-3 py-1.5 ${rag.strip}`}>
                                <Info className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="text-[11px] font-bold uppercase tracking-wider">
                                    {t(rag.qualifierKey, rag.qualifier)}
                                </span>
                            </div>
                            <div className="flex">
                                <div className={`w-1.5 flex-shrink-0 ${rag.rail}`} aria-hidden="true" />
                                <div className="flex-1 min-w-0 p-4">
                                    <div className="flex items-start gap-3">
                                        <DispIcon className={`h-7 w-7 flex-shrink-0 ${rag.accent}`} />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-lg font-bold leading-tight">{t('objects.versions.disposition_' + bannerKey, bannerLabel)}</span>
                                                {coverageDowngrade && (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                                                        <AlertTriangle className="h-3 w-3" />{t('objects.versions.coverageIncompleteBadge', 'Incomplete coverage')}
                                                    </span>
                                                )}
                                                <MalwareEngineBadge report={data} />
                                            </div>
                                            <p className="text-sm mt-0.5">{t('objects.versions.dispositionSub_' + bannerKey, bannerSub)}</p>
                                        </div>
                                    </div>

                                    {/* The three counts that actually answer "do I need to act".
                                        These REPLACE the severity row as the headline: a page whose
                                        answer is "nothing confirmed" must not lead with "Critical 155". */}
                                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
                                        <ClassCount
                                            n={confirmedN}
                                            label={t('objects.versions.countConfirmed', 'Confirmed detections')}
                                            tone="text-red-600 dark:text-red-400"
                                            hint={t('objects.versions.detectionsNote', 'Deterministic, malicious-by-nature detections (signature, known-bad hash, threat-intel IOC, ransom note, malicious URL).')}
                                        />
                                        <ClassCount
                                            n={indicatorN}
                                            label={t('objects.versions.countIndicators', 'Indicators')}
                                            tone="text-amber-600 dark:text-amber-400"
                                            hint={t('objects.versions.indicatorsNote', 'Heuristic / dual-use / structural signals, uncorroborated. Ordered by confidence — none is a confirmed detection.')}
                                        />
                                        <ClassCount
                                            n={telemetryN}
                                            label={t('objects.versions.countTelemetry', 'Telemetry')}
                                            tone="text-slate-600 dark:text-slate-300"
                                            hint={t('objects.versions.telemetryNote', 'Routine dual-use activity (admin events, VSS state changes, RDP, PowerShell). Context only — not alarming.')}
                                        />
                                        <ClassCount
                                            n={advisoryN}
                                            label={t('objects.versions.countAdvisory')}
                                            tone="text-violet-700 dark:text-violet-300"
                                            hint={t('objects.versions.advisoryNote')}
                                        />
                                        <ClassCount
                                            n={postureN}
                                            label={t('objects.versions.countPosture')}
                                            tone="text-sky-700 dark:text-sky-300"
                                            hint={t('objects.versions.postureNote')}
                                        />
                                    </div>

                                    {namedGroups.length > 0 && (
                                        <div className="mt-3 space-y-2" data-testid="headline-findings">
                                            {namedGroups.map((g) => (
                                                <ClusterRow key={g.key} group={g} expanded={expandedGroups.has(g.key)} onToggle={() => toggleGroup(g.key)} t={t as any} />
                                            ))}
                                            {namedMore > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setTab('findings')}
                                                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                                                >
                                                    {t('objects.versions.seeAllFindings', 'See all {{n}} groups', { n: totalGroups })}
                                                    <ChevronRight className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* Everything long is one click away, nothing is gone. */}
                                    {(disposition.aiSummary || bannerWhy) && (
                                        <Disclosure label={t('objects.versions.whyThisStatus', 'Why this status?')} className="mt-3">
                                            <div className="space-y-1.5">
                                                {bannerWhy && (
                                                    <>
                                                    <p className="text-xs text-muted-foreground">{t('objects.versions.dispositionWhy_' + bannerKey, bannerWhy, { engines: (disposition.key === 'incomplete' ? failedEngines : disposition.confirmedEngines).join(', ') })}</p>
                                                    {/* A confirmed detection outranks "incomplete" in the headline; the scan was still incomplete, and says so. */}
                                                    {disposition.key === 'threat' && failedEngines.length > 0 && (
                                                        <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mt-1">{t('objects.versions.dispositionAlsoIncomplete', 'This scan was also incomplete. Did not finish: {{engines}}.', { engines: failedEngines.join(', ') })}</p>
                                                    )}
                                                    </>
                                                )}
                                                {disposition.aiSummary && (
                                                    <TranslatableText as="p" className="text-xs text-muted-foreground" text={disposition.aiSummary} />
                                                )}
                                            </div>
                                        </Disclosure>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ── Coverage strip — compact, expands for the per-volume detail ── */}
                        {(coverage.source !== 'none' || copyCheck) && (
                            <div className={`mt-3 rounded-lg border p-3 ${coverage.incomplete ? 'border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40' : 'border-transparent bg-muted/40'}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <HardDrive className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                    <span className="text-xs font-semibold">{t('objects.versions.coverageTitle', 'Scan coverage')}</span>
                                    {coverage.incomplete && (
                                        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                                            <AlertTriangle className="h-3 w-3" />{t('objects.versions.coverageIncomplete', 'Incomplete')}
                                        </span>
                                    )}
                                    {coverage.source !== 'none' && <span className="text-xs text-muted-foreground">
                                        {coverage.total != null && coverage.scanned != null
                                            ? t('objects.versions.coverageScanned', 'Scanned {{scanned}} of {{total}} volumes.', { scanned: coverage.scanned, total: coverage.total })
                                            : coverage.incomplete
                                                ? t('objects.versions.coveragePartial', 'Some data could not be fully scanned.')
                                                : t('objects.versions.coverageRecorded', 'Coverage recorded for this scan.')}
                                    </span>}
                                </div>
                                {copyCheck && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t('objects.versions.copyCheck', 'Files confirmed as the ones the copy wrote: {{hash}} by checksum, {{size}} by version and size.', { hash: copyCheck.byHash, size: copyCheck.byVersionAndSize })}
                                    </p>
                                )}
                                {coverage.incomplete && (
                                    <p className="text-xs text-muted-foreground mt-1">{t('objects.versions.coverageCaveat', 'A "no threats detected" result only covers what was scanned.')}</p>
                                )}
                                {(skippedVolumes.length > 0 || coverage.notes.length > 0) && (
                                    <Disclosure
                                        label={t('objects.versions.coverageGaps', 'Coverage gaps')}
                                        count={skippedVolumes.length + coverage.notes.length}
                                        className="mt-2"
                                        defaultOpen={coverage.incomplete}
                                    >
                                        <ul className="space-y-1">
                                            {skippedVolumes.map((v, idx) => (
                                                <li key={`v${idx}`} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                                    <span className="text-amber-500 mt-px">&bull;</span>
                                                    <span><span className="font-medium">{v.device || t('objects.versions.coverageVolume', 'Volume')}</span>{v.fs ? ` (${v.fs})` : ''}{v.skip_reason ? ` — ${v.skip_reason}` : ` — ${t('objects.versions.coverageNotReached', 'not reached')}`}</span>
                                                </li>
                                            ))}
                                            {coverage.notes.slice(0, 6).map((n, idx) => (
                                                <li key={`n${idx}`} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                                    <span className="text-amber-500 mt-px">&bull;</span><span>{n}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </Disclosure>
                                )}
                                {measured.length > 0 && (
                                    <Disclosure
                                        label={t('objects.versions.measuredTitle', 'Backup files measured')}
                                        count={measured.length}
                                        className="mt-2"
                                        defaultOpen={measured.some((m) => m.state !== 'complete' || m.problems.length > 0)}
                                    >
                                        <ul className="space-y-1.5">
                                            {measured.map((m, idx) => (
                                                <li key={`m${idx}`} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                                    <span className={`mt-px ${m.state === 'complete' && m.problems.length === 0 ? 'text-slate-400' : 'text-amber-500'}`}>&bull;</span>
                                                    <span className="min-w-0 break-words">
                                                        <span className="font-medium">{m.file}</span>
                                                        {' — '}
                                                        <span className={m.state === 'complete' ? '' : 'font-semibold text-amber-700 dark:text-amber-300'}>
                                                            {m.state === 'complete'
                                                                ? t('objects.versions.measuredFull', 'every byte read')
                                                                : m.state === 'partly'
                                                                    ? t('objects.versions.measuredPartly', 'partly read')
                                                                    : t('objects.versions.measuredFailed', 'not measured')}
                                                            {m.percentRead != null ? ` (${m.percentRead}%)` : ''}
                                                        </span>
                                                        {m.error ? ` — ${m.error}` : ''}
                                                        {m.integrity ? ` — ${m.integrity}` : ''}
                                                        {m.problems.length > 0 && (
                                                            <span className="block text-amber-700 dark:text-amber-300">
                                                                {t('objects.versions.formatProblems', 'Does not match its format')}: {m.problems.join('; ')}
                                                            </span>
                                                        )}
                                                        {m.notVerified.length > 0 && (
                                                            <span className="block">
                                                                {t('objects.versions.formatNotVerified', 'Not proved by this check')}: {m.notVerified.join('; ')}
                                                            </span>
                                                        )}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </Disclosure>
                                )}
                            </div>
                        )}

                        <div className="mt-4">
                        {/* ── Overview ─────────────────────────────────────────────── */}
                        <TabsContent value="overview" className="mt-0 space-y-5">

                            {/* Active Directory leads the overview when it has findings. A DC
                                finding is not one row among many — it describes the domain. */}
                            {ad.state === 'findings' && <ADFindingsSection report={data} />}

                            {/* WHAT WAS FLAGGED — the section that answers the question people
                                actually open this dialog with. Clusters, not rows: the AD1 scan's
                                609 findings are three observations repeated per file. */}
                            {headlineClusters.length > 0 && (
                                <div>
                                    <div className="flex items-baseline justify-between gap-3 mb-2">
                                        <h3 className="text-sm font-semibold">{t('objects.versions.whatWasFlagged', 'What was flagged')}</h3>
                                        <span className="text-xs text-muted-foreground">
                                            {t('objects.versions.groupedSummary', '{{findings}} findings in {{groups}} groups', { findings: totalFindings, groups: totalGroups })}
                                        </span>
                                    </div>
                                    <div className="space-y-2">
                                        {headlineClusters.map((g) => (
                                            <ClusterRow key={g.key} group={g} expanded={expandedGroups.has(g.key)} onToggle={() => toggleGroup(g.key)} t={t as any} />
                                        ))}
                                    </div>
                                    {totalGroups > headlineClusters.length && (
                                        <button
                                            type="button"
                                            onClick={() => setTab('findings')}
                                            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                                        >
                                            {t('objects.versions.seeAllFindings', 'See all {{n}} groups', { n: totalGroups })}
                                            <ChevronRight className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* FLAGGED FOR REVIEW — advisory. An interpretation or an observed change, not
                                a detection: shown, in its own section, apart from the threat picture
                                above and ahead of the hardening backlog below. */}
                            {advisoryGroups.length > 0 && (
                                <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3 dark:border-violet-900/50 dark:bg-violet-950/20">
                                    <div className="flex items-baseline gap-2 mb-1">
                                        <Eye className="h-4 w-4 self-center text-violet-700 dark:text-violet-300" />
                                        <h3 className="text-sm font-semibold">{t('objects.versions.advisoryTitle')}</h3>
                                        <span className="text-xs font-semibold tabular-nums text-violet-700 dark:text-violet-300">{advisoryN}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-2">{t('objects.versions.advisoryNote')}</p>
                                    <div className="space-y-2">{renderGroups(advisoryGroups)}</div>
                                </div>
                            )}

                            {/* CONFIGURATION HARDENING — posture. Not escalating is not hiding: it is
                                on the Overview, in its own clearly labelled advisory section, apart
                                from the threat picture above. */}
                            {postureGroups.length > 0 && (
                                <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 dark:border-sky-900/50 dark:bg-sky-950/20">
                                    <div className="flex items-baseline gap-2 mb-1">
                                        <Wrench className="h-4 w-4 self-center text-sky-700 dark:text-sky-300" />
                                        <h3 className="text-sm font-semibold">{t('objects.versions.postureTitle')}</h3>
                                        <span className="text-xs font-semibold tabular-nums text-sky-700 dark:text-sky-300">{postureN}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-2">{t('objects.versions.postureNote')}</p>
                                    <div className="space-y-2">{renderGroups(postureGroups)}</div>
                                </div>
                            )}

                            {/* Pipeline error */}
                            {pipelineFindings.length > 0 && pipelineFindings.map((f: any, i: number) => (
                                <div key={i} className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3">
                                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-medium text-sm">{f.title}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">{f.description}</div>
                                    </div>
                                </div>
                            ))}

                            {/* AI analysis — headline only; the narrative is a tab away. */}
                            <div className="rounded-lg border p-4">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <Sparkles className="h-4 w-4 text-violet-500" />
                                    <h3 className="text-sm font-semibold">{t('objects.versions.aiAnalysis', 'AI analysis')}</h3>
                                    <span className="inline-flex items-center rounded-full bg-black/5 dark:bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">{t('objects.versions.decisionSupport', 'Decision support')}</span>
                                    {ai?.confidence != null && (
                                        <span className="text-xs text-muted-foreground">{t('objects.versions.confidence', 'confidence')}: {typeof ai.confidence === 'number' ? `${Math.round(ai.confidence * 100)}%` : String(ai.confidence)}</span>
                                    )}
                                </div>
                                {aiHasContent ? (
                                    <div className="space-y-2 text-sm">
                                        {ai.assessment && <p className="font-semibold">{ASSESSMENT_LABEL[String(ai.assessment)] || String(ai.assessment)}</p>}
                                        {ai.status_line && <p className="text-muted-foreground">{ai.status_line}</p>}
                                        {aiHasDetail && (
                                            <button type="button" onClick={() => setTab('detailed')} className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 hover:underline dark:text-violet-400">
                                                {t('objects.versions.viewFullAnalysis', 'View full analysis')}
                                                <ChevronRight className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        {aiErrored
                                            ? t('objects.versions.aiFailed', 'AI analysis could not be completed for this scan. The disposition above is derived from the scan status and engine signals.')
                                            : t('objects.versions.aiNotAvailable', 'AI analysis is not available for this scan. The disposition above is derived from the scan status and engine signals.')}
                                    </p>
                                )}
                            </div>

                            {/* Engine summary */}
                            <div className="grid grid-cols-3 gap-2">
                                <div className="rounded-lg border p-3 text-center">
                                    <div className="text-xl font-bold tabular-nums">{enginesRan}</div>
                                    <div className="text-xs text-muted-foreground">{t('objects.versions.enginesRan', 'engines ran')}</div>
                                </div>
                                <div className="rounded-lg border p-3 text-center">
                                    <div className={`text-xl font-bold tabular-nums ${enginesWithFindings.length > 0 ? (isThreat ? 'text-red-600' : 'text-blue-600') : ''}`}>{enginesWithFindings.length}</div>
                                    <div className="text-xs text-muted-foreground">{t('objects.versions.enginesWithFindings', 'reported findings')}</div>
                                </div>
                                <div className="rounded-lg border p-3 text-center">
                                    <div className="text-xl font-bold tabular-nums text-muted-foreground">{enginesNotRun}</div>
                                    <div className="text-xs text-muted-foreground">{t('objects.versions.enginesNotRun', 'not run')}</div>
                                </div>
                            </div>

                            {/* Severity — DEMOTED, and behind a disclosure.
                                It is genuinely useful to an analyst and actively misleading as a
                                headline: "Critical 155" next to "0 confirmed detections" is a
                                contradiction the reader has to resolve, and most will resolve it
                                the alarming way. It stays available; it stops shouting. */}
                            {totalFindings != null && totalFindings > 0 && (
                                <Disclosure label={t('objects.versions.severityBreakdown', 'Severity breakdown of individual findings')}>
                                    <div className="flex flex-wrap gap-2">
                                        {SEVERITY_ORDER.map((sev) => {
                                            const count = severityCounts[sev] ?? 0
                                            const config = SEVERITY_CONFIG[sev]
                                            const Icon = config.icon
                                            return (
                                                <div key={sev} className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 ${isThreat ? config.color : config.calm}`}>
                                                    <Icon className="h-3.5 w-3.5" />
                                                    <span className="text-xs font-medium">{config.label}</span>
                                                    <span className="text-sm font-bold tabular-nums">{count}</span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1.5">{t('objects.versions.severityNote', 'These are severities of individual findings, not confirmed threats.')}</p>
                                </Disclosure>
                            )}

                            {/* Device information */}
                            {Object.values(metadata).some((v) => v != null && String(v).trim() !== '') && (
                                <Disclosure label={t('objects.versions.deviceInformation', 'Device information')}>
                                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                                        {([['device_name', 'Device'], ['manufacturer', 'Manufacturer'], ['model', 'Model'], ['software_version', 'Software'], ['ip_address', 'IP Address'], ['default_gateway', 'Gateway'], ['software_image_file', 'Image']] as const).map(([k, label]) => metadata[k] ? (
                                            <div key={k} className="contents">
                                                <span className="text-muted-foreground">{label}</span>
                                                <span className="font-medium truncate">{metadata[k]}</span>
                                            </div>
                                        ) : null)}
                                    </div>
                                </Disclosure>
                            )}

                            {/* Limitations + decision ownership — ALWAYS visible, never collapsed.
                                These are the standing caveats on the RAG itself, so they sit in
                                the open at the foot of the overview. */}
                            <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
                                <p className="text-xs text-muted-foreground">
                                    <span className="font-semibold">{t('objects.versions.limitations', 'Limitations')}: </span>
                                    {ai?.limitations || t('objects.versions.limitationsDefault', 'Scanning detects known threats only. It cannot guarantee the absence of zero-day, novel, dormant, fileless, or obfuscated threats. Results reflect engine definitions current at scan time and the scope of data scanned.')}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    <span className="font-semibold">{t('objects.versions.decisionOwnership', 'Decision ownership')}: </span>
                                    {ai?.decision_ownership || t('objects.versions.decisionOwnershipDefault', 'Restore decisions rest with your security team. This report is decision-support, not a determination that the object is free of threats.')}
                                </p>
                            </div>
                        </TabsContent>

                        {/* ── Detailed analysis (the full AI narrative) ─────────────── */}
                        {aiHasDetail && (
                            <TabsContent value="detailed" className="mt-0">
                                <div className="rounded-lg border p-4 space-y-2 text-sm">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <Sparkles className="h-4 w-4 text-violet-500" />
                                        <h3 className="text-sm font-semibold">{t('objects.versions.aiAnalysis', 'AI analysis')}</h3>
                                        <span className="inline-flex items-center rounded-full bg-black/5 dark:bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">{t('objects.versions.decisionSupport', 'Decision support')}</span>
                                        {ai?.confidence != null && (
                                            <span className="text-xs text-muted-foreground">{t('objects.versions.confidence', 'confidence')}: {typeof ai.confidence === 'number' ? `${Math.round(ai.confidence * 100)}%` : String(ai.confidence)}</span>
                                        )}
                                    </div>
                                    {ai?.assessment && <p className="font-semibold">{ASSESSMENT_LABEL[String(ai.assessment)] || String(ai.assessment)}</p>}
                                    {ai?.status_line && <p className="text-muted-foreground">{ai.status_line}</p>}
                                    {ai?.summary && <TranslatableText as="p" className="font-medium" text={ai.summary} />}
                                    {ai?.attack_narrative && <TranslatableText as="p" className="text-muted-foreground" text={ai.attack_narrative} />}
                                    {Array.isArray(ai?.key_findings) && ai.key_findings.length > 0 && (
                                        <div>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-2 mb-1">{t('objects.versions.keyFindings', 'Key findings')}</div>
                                            <ul className="space-y-1">
                                                {ai.key_findings.map((k: any, i: number) => (
                                                    <li key={i} className="text-sm"><span className="font-medium"><TranslatableText text={k.title} /></span>{k.engine && <span className="text-xs text-muted-foreground"> · {k.engine}</span>}{k.why_it_matters && <span className="text-muted-foreground"> — <TranslatableText text={k.why_it_matters} /></span>}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {Array.isArray(ai?.likely_false_positives) && ai.likely_false_positives.length > 0 && (
                                        <div>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-2 mb-1">{t('objects.versions.likelyFalsePositives', 'Likely false positives')}</div>
                                            <ul className="space-y-1">
                                                {ai.likely_false_positives.map((k: any, i: number) => (
                                                    <li key={i} className="text-sm"><span className="font-medium"><TranslatableText text={k.title} /></span>{k.reason && <span className="text-muted-foreground"> — <TranslatableText text={k.reason} /></span>}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {(ai?.limitations || ai?.decision_ownership) && (
                                        <div className="mt-3 space-y-1.5 border-t pt-2.5">
                                            {ai.limitations && (
                                                <p className="text-xs text-muted-foreground"><span className="font-semibold">{t('objects.versions.limitations', 'Limitations')}: </span>{ai.limitations}</p>
                                            )}
                                            {ai.decision_ownership && (
                                                <p className="text-xs text-muted-foreground"><span className="font-semibold">{t('objects.versions.decisionOwnership', 'Decision ownership')}: </span>{ai.decision_ownership}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </TabsContent>
                        )}

                        {/* ── Findings ─────────────────────────────────────────────── */}
                        <TabsContent value="findings" className="mt-0">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input placeholder={t('common.search', 'Search')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-sm" />
                                </div>
                                {/* Grouped by default; the flat list stays one click away. */}
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 gap-1.5 flex-shrink-0"
                                    onClick={() => setGrouped((g) => !g)}
                                    title={grouped
                                        ? t('objects.versions.showEveryFinding', 'Show every finding individually')
                                        : t('objects.versions.groupSimilarHint', 'Group findings that are the same observation repeated per file')}
                                >
                                    {grouped ? <Layers className="h-3.5 w-3.5" /> : <List className="h-3.5 w-3.5" />}
                                    <span className="text-xs">{grouped ? t('objects.versions.grouped', 'Grouped') : t('objects.versions.ungrouped', 'All findings')}</span>
                                </Button>
                            </div>

                            {/* Active Directory, always — including the quiet states. This is the
                                one place a reader can find out whether the AD audit ran at all,
                                which is a different question from whether it found anything. */}
                            <section className="mb-4">
                                <ADFindingsSection report={data} />
                            </section>

                            {/* Detections (confirmed) — deterministic, malicious-by-nature. Drive Red. */}
                            {confirmedFindings.length > 0 && (
                                <section className="mb-4">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <ShieldAlert className="h-4 w-4 text-red-600" />
                                        <h3 className="text-sm font-semibold">{t('objects.versions.detections', 'Detections')}</h3>
                                        <span className="text-xs font-semibold tabular-nums text-red-600">{classCounts.confirmed ?? confirmedFindings.length}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-2">{t('objects.versions.detectionsNote', 'Deterministic, malicious-by-nature detections (signature, known-bad hash, threat-intel IOC, ransom note, malicious URL).')}</p>
                                    <div className="space-y-2">{renderGroups(grouped ? confirmedGroups : asSingles(confirmedFindings))}</div>
                                </section>
                            )}

                            {/* Indicators — heuristic / dual-use, uncorroborated. Ordered by confidence. */}
                            {(indicatorFindings.length > 0 || coverageFindings.length > 0) && (
                                <section className="mb-4">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <Info className="h-4 w-4 text-amber-600" />
                                        <h3 className="text-sm font-semibold">{t('objects.versions.indicators', 'Indicators')}</h3>
                                        <span className="text-xs font-semibold tabular-nums text-amber-600">{classCounts.indicator ?? (indicatorFindings.length + coverageFindings.length)}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-2">{t('objects.versions.indicatorsNote', 'Heuristic / dual-use / structural signals, uncorroborated. Ordered by confidence — none is a confirmed detection.')}</p>
                                    <div className="space-y-2">{renderGroups(grouped ? indicatorGroups : asSingles(indicatorFindings))}</div>
                                    {coverageFindings.length > 0 && (
                                        <div className="mt-3 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/10 p-3">
                                            <div className="flex items-center gap-2 mb-1">
                                                <AlertCircle className="h-4 w-4 text-amber-600" />
                                                <h4 className="text-xs font-semibold">{t('objects.versions.coverageNotices', 'Files not fully inspected — review')}</h4>
                                                <span className="text-xs text-muted-foreground tabular-nums">{coverageFindings.length}</span>
                                            </div>
                                            <p className="text-xs text-muted-foreground mb-2">{t('objects.versions.coverageNote', 'Scan-coverage notices (a file exceeded scan limits or timed out) — coverage gaps to review, not threats.')}</p>
                                            <div className="space-y-2">{renderGroups(grouped ? coverageGroups : asSingles(coverageFindings))}</div>
                                        </div>
                                    )}
                                </section>
                            )}

                            {/* Flagged for review — advisory. Never moves the status. */}
                            {advisoryFindings.length > 0 && (
                                <section className="mb-4">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <Eye className="h-4 w-4 text-violet-700 dark:text-violet-300" />
                                        <h3 className="text-sm font-semibold">{t('objects.versions.advisoryTitle')}</h3>
                                        <span className="text-xs font-semibold tabular-nums text-violet-700 dark:text-violet-300">{advisoryN}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-2">{t('objects.versions.advisoryNote')}</p>
                                    <div className="space-y-2">{renderGroups(grouped ? advisoryGroups : asSingles(advisoryFindings))}</div>
                                </section>
                            )}

                            {/* Configuration hardening — posture. Never moves the status. */}
                            {postureFindings.length > 0 && (
                                <section className="mb-4">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <Wrench className="h-4 w-4 text-sky-700 dark:text-sky-300" />
                                        <h3 className="text-sm font-semibold">{t('objects.versions.postureTitle')}</h3>
                                        <span className="text-xs font-semibold tabular-nums text-sky-700 dark:text-sky-300">{postureN}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-2">{t('objects.versions.postureNote')}</p>
                                    <div className="space-y-2">{renderGroups(grouped ? postureGroups : asSingles(postureFindings))}</div>
                                </section>
                            )}

                            {/* Telemetry — routine dual-use. Context only; never alarming. */}
                            {telemetryFindings.length > 0 && (
                                <section className="mb-4">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <CircleMinus className="h-4 w-4 text-slate-400" />
                                        <h3 className="text-sm font-semibold">{t('objects.versions.telemetry', 'Telemetry')}</h3>
                                        <span className="text-xs text-muted-foreground tabular-nums">{classCounts.telemetry ?? telemetryFindings.length}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-2">{t('objects.versions.telemetryNote', 'Routine dual-use activity (admin events, VSS state changes, RDP, PowerShell). Context only — not alarming.')}</p>
                                    <div className="space-y-2">{renderGroups(grouped ? telemetryGroups : asSingles(telemetryFindings))}</div>
                                </section>
                            )}

                            {confirmedFindings.length === 0 && indicatorFindings.length === 0 && coverageFindings.length === 0 && telemetryFindings.length === 0 && advisoryFindings.length === 0 && postureFindings.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-10 text-center">
                                    <ShieldQuestion className="h-8 w-8 text-slate-400 mb-2" />
                                    <p className="text-sm text-muted-foreground">{search ? t('objects.versions.noMatchingFindings', 'No findings match your search.') : t('objects.versions.noFindingsReported', 'No findings were reported.')}</p>
                                </div>
                            )}
                        </TabsContent>

                        {/* ── ATT&CK ───────────────────────────────────────────────── */}
                        {hasAttack && (
                            <TabsContent value="attack" className="mt-0 space-y-4">
                                <div className="flex items-center gap-2">
                                    <img src="/logos/MITRE_ATTACK_logo_Lockup-black.svg" alt="MITRE ATT&CK" className="h-4 dark:hidden" />
                                    <img src="/logos/MITRE_ATTACK_logo_Lockup-gray.svg" alt="MITRE ATT&CK" className="h-4 hidden dark:block" />
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {isThreat
                                        ? t('objects.versions.attackObserved', 'Tactics and techniques observed in the confirmed findings.')
                                        : t('objects.versions.attackDualUse', 'Behavioural / dual-use signals observed — none corroborated by a confirmed detection.')}
                                </p>
                                {observedTactics.length > 0 && (
                                    <div className="flex flex-wrap items-center gap-2">
                                        {observedTactics.map((tactic) => (
                                            <span key={tactic} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300">
                                                <Crosshair className="h-3 w-3" />{tactic}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {reportTechniques.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {reportTechniques.map((tt) => {
                                            const tactic = tacticForTechnique(tt.id)
                                            return (
                                                <span key={tt.id} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
                                                    <MitreBadge id={tt.id} name={tt.name} />
                                                    {tactic && <span className="text-muted-foreground">{tactic}</span>}
                                                    {tt.count != null && tt.count > 1 && <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-muted-foreground">{tt.count}</span>}
                                                </span>
                                            )
                                        })}
                                    </div>
                                )}
                            </TabsContent>
                        )}

                        {/* ── Engines ──────────────────────────────────────────────── */}
                        <TabsContent value="engines" className="mt-0">
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-muted/50 border-b">
                                            <th className="text-left px-3 py-2 font-medium">{t('objects.versions.engine', 'Engine')}</th>
                                            <th className="text-center px-3 py-2 font-medium">{t('objects.versions.status', 'Status')}</th>
                                            <th className="text-center px-3 py-2 font-medium">{t('objects.versions.findings', 'Findings')}</th>
                                            <th className="text-center px-3 py-2 font-medium">{t('objects.versions.duration', 'Duration')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {engineRows.map((row, idx: number) => {
                                            const name = row.name
                                            const engine = row
                                            // The row's canonical name: older scans send `ad_analysis`
                                            // as a row, and the roster derives rows of its own.
                                            const rowKey = canonicalEngine(name)
                                            return (
                                            <tr key={name} className={idx % 2 === 0 ? '' : 'bg-muted/20'}>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="font-medium">{engineLabel(name)}</span>
                                                        {isConfirmedCapable(name) && <span className="text-[10px] rounded bg-slate-100 dark:bg-slate-800 px-1 text-muted-foreground" title={t('objects.versions.iocBadgeHint')}>IOC</span>}
                                                        {row.derived && (
                                                            <span className="text-[10px] rounded bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300 px-1" title={row.derivedFrom || ''}>
                                                                {t('objects.versions.engineDerived', 'not in summary')}
                                                            </span>
                                                        )}
                                                        <span className="relative group/tip inline-flex">
                                                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" />
                                                            <span className="absolute left-6 top-1/2 -translate-y-1/2 z-[400] hidden group-hover/tip:block w-80 px-2.5 py-1.5 rounded-md border bg-popover text-popover-foreground text-xs leading-relaxed shadow-md">
                                                                {t('objects.versions.engineDesc.' + rowKey, ENGINE_DESCRIPTIONS[rowKey] || `${name} scanning engine`)}
                                                            </span>
                                                        </span>
                                                    </div>
                                                    {/* "not applicable" without its reason reads as lost coverage.
                                                        ClamAV on a premium scan did not fail — it was replaced. */}
                                                    {row.reason && (
                                                        <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{row.reason}</div>
                                                    )}
                                                    {row.derived && row.derivedFrom && (
                                                        <div className="text-[11px] text-blue-700 dark:text-blue-300 mt-0.5 leading-snug">{row.derivedFrom}</div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-center"><EngineStatusBadge status={engine.status} /></td>
                                                <td className="px-3 py-2 text-center">
                                                    {engine.findings != null && engine.findings > 0 ? (
                                                        <span className="text-xs font-semibold tabular-nums">{engine.findings}</span>
                                                    ) : engine.findings === 0 ? <span className="text-xs text-muted-foreground">0</span> : <span className="text-xs text-muted-foreground">-</span>}
                                                </td>
                                                <td className="px-3 py-2 text-center"><span className="text-xs text-muted-foreground">{formatDuration(engine.duration)}</span></td>
                                            </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">{t('objects.versions.iocNote')}</p>
                        </TabsContent>
                        </div>
                    </FeatheredScrollArea>
                </Tabs>
            </DialogContent>
        </Dialog>
        {objectId && (
            <RaiseIncidentDialog
                open={incidentOpen}
                onOpenChange={setIncidentOpen}
                objectId={objectId}
                context={incidentCtx.data || null}
                prefill={incidentPrefill}
            />
        )}
        </>
    )
}
